import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MapPin, Search, Loader2, Check, X, Globe } from 'lucide-react';
import { toast } from 'sonner';

// Country and State/Province data
const COUNTRIES = [
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'OTHER', name: 'Other', flag: '🌍' },
];

const STATES_BY_COUNTRY = {
  AU: [
    { code: 'NSW', name: 'New South Wales' },
    { code: 'VIC', name: 'Victoria' },
    { code: 'QLD', name: 'Queensland' },
    { code: 'WA', name: 'Western Australia' },
    { code: 'SA', name: 'South Australia' },
    { code: 'TAS', name: 'Tasmania' },
    { code: 'NT', name: 'Northern Territory' },
    { code: 'ACT', name: 'Australian Capital Territory' },
  ],
  NZ: [
    { code: 'NTL', name: 'Northland' },
    { code: 'AUK', name: 'Auckland' },
    { code: 'WKO', name: 'Waikato' },
    { code: 'BOP', name: 'Bay of Plenty' },
    { code: 'GIS', name: 'Gisborne' },
    { code: 'HKB', name: "Hawke's Bay" },
    { code: 'TKI', name: 'Taranaki' },
    { code: 'MWT', name: 'Manawatū-Whanganui' },
    { code: 'WGN', name: 'Wellington' },
    { code: 'TAS', name: 'Tasman' },
    { code: 'NSN', name: 'Nelson' },
    { code: 'MBH', name: 'Marlborough' },
    { code: 'WTC', name: 'West Coast' },
    { code: 'CAN', name: 'Canterbury' },
    { code: 'OTA', name: 'Otago' },
    { code: 'STL', name: 'Southland' },
  ],
  US: [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  ],
  GB: [
    { code: 'ENG', name: 'England' }, { code: 'SCT', name: 'Scotland' },
    { code: 'WLS', name: 'Wales' }, { code: 'NIR', name: 'Northern Ireland' },
  ],
  CA: [
    { code: 'AB', name: 'Alberta' }, { code: 'BC', name: 'British Columbia' },
    { code: 'MB', name: 'Manitoba' }, { code: 'NB', name: 'New Brunswick' },
    { code: 'NL', name: 'Newfoundland and Labrador' }, { code: 'NS', name: 'Nova Scotia' },
    { code: 'ON', name: 'Ontario' }, { code: 'PE', name: 'Prince Edward Island' },
    { code: 'QC', name: 'Quebec' }, { code: 'SK', name: 'Saskatchewan' },
    { code: 'NT', name: 'Northwest Territories' }, { code: 'NU', name: 'Nunavut' }, { code: 'YT', name: 'Yukon' },
  ],
  ZA: [
    { code: 'EC', name: 'Eastern Cape' }, { code: 'FS', name: 'Free State' },
    { code: 'GP', name: 'Gauteng' }, { code: 'KZN', name: 'KwaZulu-Natal' },
    { code: 'LP', name: 'Limpopo' }, { code: 'MP', name: 'Mpumalanga' },
    { code: 'NC', name: 'Northern Cape' }, { code: 'NW', name: 'North West' },
    { code: 'WC', name: 'Western Cape' },
  ],
};

const AddYardDialog = ({ open, onClose, onAdd, beekeepers = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    street_address: '',
    suburb: '',
    state: '',
    state_name: '',
    postcode: '',
    country: 'AU',
    country_name: 'Australia',
    gps_lat: '',
    gps_lng: '',
    notes: '',
    owner_name: '',
    owner_surname: '',
    owner_mobile: '',
    assigned_beekeeper_id: '',
  });
  const [suburbSuggestions, setSuburbSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [locationVerified, setLocationVerified] = useState(false);

  // Get states for selected country
  const availableStates = STATES_BY_COUNTRY[formData.country] || [];

  // Search for suburb/city within the selected state/country
  const searchSuburb = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuburbSuggestions([]);
      return;
    }
    
    setSearching(true);
    try {
      // Build search query with country context
      const countryName = COUNTRIES.find(c => c.code === formData.country)?.name || '';
      const stateName = formData.state_name || '';
      const searchQuery = `${query}, ${stateName}, ${countryName}`.replace(/, ,/g, ',');
      
      const response = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(searchQuery)}&count=8&language=en&format=json`
      );
      
      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        // Filter results to match selected country
        const filtered = data.results.filter(r => 
          !formData.country || formData.country === 'OTHER' || 
          r.country_code === formData.country
        );
        
        setSuburbSuggestions(filtered.map(r => ({
          name: r.name,
          admin1: r.admin1,
          admin2: r.admin2,
          country: r.country,
          country_code: r.country_code,
          latitude: r.latitude,
          longitude: r.longitude,
          postcode: r.postcodes?.[0] || '',
        })));
      } else {
        setSuburbSuggestions([]);
      }
    } catch (err) {
    } finally {
      setSearching(false);
    }
  }, [formData.country, formData.state_name]);

  // Select a suburb from suggestions
  const selectSuburb = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      suburb: suggestion.name,
      postcode: suggestion.postcode || prev.postcode,
      gps_lat: suggestion.latitude.toString(),
      gps_lng: suggestion.longitude.toString(),
    }));
    setSuburbSuggestions([]);
    setLocationVerified(true);
    toast.success(`Location set: ${suggestion.name} (${suggestion.latitude.toFixed(4)}, ${suggestion.longitude.toFixed(4)})`);
  };

  // Handle country change
  const handleCountryChange = (countryCode) => {
    const country = COUNTRIES.find(c => c.code === countryCode);
    setFormData(prev => ({
      ...prev,
      country: countryCode,
      country_name: country?.name || '',
      state: '',
      state_name: '',
      suburb: '',
      postcode: '',
      gps_lat: '',
      gps_lng: '',
    }));
    setLocationVerified(false);
  };

  // Handle state change
  const handleStateChange = (stateCode) => {
    const state = availableStates.find(s => s.code === stateCode);
    setFormData(prev => ({
      ...prev,
      state: stateCode,
      state_name: state?.name || stateCode,
      suburb: '',
      postcode: '',
      gps_lat: '',
      gps_lng: '',
    }));
    setLocationVerified(false);
  };

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }
    
    setSearching(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setFormData(prev => ({
          ...prev,
          gps_lat: latitude.toFixed(6),
          gps_lng: longitude.toFixed(6)
        }));
        
        // Reverse geocode to get location details
        try {
          const response = await fetch(
            `https://geocoding-api.open-meteo.com/v1/search?name=${latitude.toFixed(4)},${longitude.toFixed(4)}&count=1`
          );
          
          // Also get timezone info
          const tzResponse = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m&timezone=auto`
          );
          const tzData = await tzResponse.json();
          
          if (tzData.timezone) {
            const cityFromTz = tzData.timezone.split('/').pop().replace(/_/g, ' ');
            setFormData(prev => ({
              ...prev,
              suburb: cityFromTz,
            }));
          }
          
          setLocationVerified(true);
          toast.success('GPS location captured');
        } catch (err) {
          toast.success('GPS coordinates captured');
        }
        setSearching(false);
      },
      (err) => {
        toast.error('Could not get location');
        setSearching(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Geocode address to coordinates
  const geocodeAddress = async () => {
    const locationParts = [
      formData.street_address,
      formData.suburb,
      formData.state_name || formData.state,
      formData.postcode,
      formData.country_name
    ].filter(Boolean);
    
    const fullAddress = locationParts.join(', ');
    
    if (!fullAddress || fullAddress.length < 5) {
      toast.error('Please enter an address first');
      return;
    }
    
    setSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        setFormData(prev => ({
          ...prev,
          gps_lat: parseFloat(lat).toFixed(6),
          gps_lng: parseFloat(lon).toFixed(6)
        }));
        setLocationVerified(true);
        toast.success(`Location found: ${data[0].display_name.substring(0, 50)}...`);
      } else {
        toast.error('Could not find coordinates for this address. Try using GPS instead.');
      }
    } catch (err) {
      toast.error('Failed to lookup address');
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If no GPS coordinates and we have an address, try to geocode it first
    if ((!formData.gps_lat || formData.gps_lat === '0' || formData.gps_lat === 0) && formData.street_address) {
      setSearching(true);
      try {
        const locationParts = [
          formData.street_address,
          formData.suburb,
          formData.state_name || formData.state,
          formData.postcode,
          formData.country_name
        ].filter(Boolean);
        
        const fullAddress = locationParts.join(', ');
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`
        );
        const data = await response.json();
        
        if (data && data.length > 0) {
          const { lat, lon } = data[0];
          formData.gps_lat = parseFloat(lat).toFixed(6);
          formData.gps_lng = parseFloat(lon).toFixed(6);
        }
      } catch (err) {
      } finally {
        setSearching(false);
      }
    }
    
    // Build full location string
    const locationParts = [
      formData.street_address,
      formData.suburb,
      formData.state,
      formData.postcode,
      formData.country_name
    ].filter(Boolean);
    
    const fullLocation = locationParts.join(', ');
    
    onAdd({
      ...formData,
      location: fullLocation,
      address: formData.street_address,
      latitude: formData.gps_lat ? parseFloat(formData.gps_lat) : null,
      longitude: formData.gps_lng ? parseFloat(formData.gps_lng) : null,
      gps_lat: formData.gps_lat ? parseFloat(formData.gps_lat) : 0,
      gps_lng: formData.gps_lng ? parseFloat(formData.gps_lng) : 0,
    });
    
    // Reset form
    setFormData({ 
      name: '', street_address: '', suburb: '', state: '', state_name: '', 
      postcode: '', country: 'AU', country_name: 'Australia',
      gps_lat: '', gps_lng: '', notes: '', owner_name: '', owner_surname: '', 
      owner_mobile: '', assigned_beekeeper_id: '' 
    });
    setLocationVerified(false);
  };

  const handleBeekeeperSelect = (value) => {
    if (value === 'none') {
      setFormData({ ...formData, assigned_beekeeper_id: '', owner_name: '', owner_surname: '', owner_mobile: '' });
    } else {
      const beekeeper = beekeepers.find(b => b.id === value);
      if (beekeeper) {
        const fullName = beekeeper.name || `${beekeeper.first_name || ''} ${beekeeper.last_name || ''}`.trim();
        const nameParts = fullName.split(' ');
        setFormData({
          ...formData,
          assigned_beekeeper_id: value,
          owner_name: nameParts[0] || '',
          owner_surname: nameParts.slice(1).join(' ') || '',
          owner_mobile: beekeeper.phone || beekeeper.mobile_phone || ''
        });
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto" data-testid="add-yard-dialog">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-xl sm:text-2xl font-bold text-stone-800 font-heading">
            Add New Apiary
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Apiary Name */}
          <div>
            <Label htmlFor="yard_name" className="text-sm text-stone-700 font-medium">
              Apiary Name *
            </Label>
            <Input
              id="yard_name"
              placeholder="e.g., North Garden, East Field"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              data-testid="yard-name-input"
              required
              className="h-12 bg-stone-50 border-stone-200 rounded-xl text-base mt-1"
            />
          </div>

          {/* Location Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm text-stone-700 font-semibold flex items-center gap-2">
                <Globe className="h-4 w-4 text-blue-600" />
                Location Details
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={getCurrentLocation}
                disabled={searching}
                className="text-xs h-8"
              >
                {searching ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <MapPin className="h-3 w-3 mr-1" />}
                Use GPS
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={geocodeAddress}
                disabled={searching || !formData.suburb}
                className="text-xs h-8"
              >
                {searching ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
                Lookup Address
              </Button>
            </div>

            {/* Country & State Row */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-xs text-stone-600">Country *</Label>
                <Select value={formData.country} onValueChange={handleCountryChange}>
                  <SelectTrigger className="h-11 bg-white border-stone-200 rounded-lg mt-1">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        <span className="flex items-center gap-2">
                          <span>{c.flag}</span>
                          <span>{c.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-stone-600">State/Province *</Label>
                {availableStates.length > 0 ? (
                  <Select value={formData.state} onValueChange={handleStateChange}>
                    <SelectTrigger className="h-11 bg-white border-stone-200 rounded-lg mt-1">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableStates.map(s => (
                        <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value, state_name: e.target.value })}
                    placeholder="Enter state/province"
                    className="h-11 bg-white border-stone-200 rounded-lg mt-1"
                  />
                )}
              </div>
            </div>

            {/* Suburb Search */}
            <div className="mb-3">
              <Label className="text-xs text-stone-600">Suburb/City *</Label>
              <div className="relative mt-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    placeholder={formData.state ? `Search suburbs in ${formData.state_name || formData.state}...` : "Select state first"}
                    value={formData.suburb}
                    onChange={(e) => {
                      setFormData({ ...formData, suburb: e.target.value });
                      setLocationVerified(false);
                      searchSuburb(e.target.value);
                    }}
                    disabled={!formData.state && formData.country !== 'OTHER'}
                    className="h-11 pl-10 pr-10 bg-white border-stone-200 rounded-lg"
                  />
                  {locationVerified && <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />}
                  {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-blue-500" />}
                </div>

                {/* Suburb Suggestions */}
                {suburbSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-stone-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {suburbSuggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => selectSuburb(suggestion)}
                        className="w-full px-4 py-3 text-left hover:bg-blue-50 flex items-start gap-3 border-b border-stone-100 last:border-0"
                      >
                        <MapPin className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-stone-800">{suggestion.name}</div>
                          <div className="text-sm text-stone-500">
                            {suggestion.admin1 && `${suggestion.admin1}, `}{suggestion.country}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Postcode & Street */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-xs text-stone-600">Postcode</Label>
                <Input
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  placeholder="3000"
                  className="h-10 bg-white border-stone-200 rounded-lg text-sm mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-stone-600">Street Address (optional)</Label>
                <Input
                  value={formData.street_address}
                  onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                  placeholder="123 Bee Street"
                  className="h-10 bg-white border-stone-200 rounded-lg text-sm mt-1"
                />
              </div>
            </div>

            {/* GPS Coordinates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-600">Latitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.gps_lat}
                  onChange={(e) => setFormData({ ...formData, gps_lat: e.target.value })}
                  placeholder="-33.8688"
                  className="h-10 bg-white border-stone-200 rounded-lg text-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Longitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.gps_lng}
                  onChange={(e) => setFormData({ ...formData, gps_lng: e.target.value })}
                  placeholder="151.2093"
                  className="h-10 bg-white border-stone-200 rounded-lg text-sm mt-1"
                />
              </div>
            </div>

            {formData.gps_lat && formData.gps_lng && (
              <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                <Check className="h-3 w-3" />
                GPS: {parseFloat(formData.gps_lat).toFixed(4)}, {parseFloat(formData.gps_lng).toFixed(4)}
              </div>
            )}
          </div>

          {/* Beekeeper Assignment */}
          {beekeepers.length > 0 && (
            <div>
              <Label className="text-sm text-stone-700 font-medium">Assign Beekeeper</Label>
              <Select value={formData.assigned_beekeeper_id || 'none'} onValueChange={handleBeekeeperSelect}>
                <SelectTrigger className="h-12 bg-stone-50 border-stone-200 rounded-xl text-base mt-1">
                  <SelectValue placeholder="Select a beekeeper..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- No beekeeper --</SelectItem>
                  {beekeepers.map(bk => (
                    <SelectItem key={bk.id} value={bk.id}>
                      {bk.name || `${bk.first_name || ''} ${bk.last_name || ''}`.trim() || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Owner Contact */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <Label className="text-sm text-stone-700 font-semibold mb-3 block">📞 Owner Contact</Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-600">First Name</Label>
                <Input
                  value={formData.owner_name}
                  onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                  placeholder="John"
                  className="h-10 bg-white border-stone-200 rounded-lg text-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-stone-600">Surname</Label>
                <Input
                  value={formData.owner_surname}
                  onChange={(e) => setFormData({ ...formData, owner_surname: e.target.value })}
                  placeholder="Smith"
                  className="h-10 bg-white border-stone-200 rounded-lg text-sm mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-stone-600">Mobile</Label>
                <Input
                  value={formData.owner_mobile}
                  onChange={(e) => setFormData({ ...formData, owner_mobile: e.target.value })}
                  placeholder="0400 000 000"
                  className="h-10 bg-white border-stone-200 rounded-lg text-sm mt-1"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-sm text-stone-700 font-medium">Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              className="bg-stone-50 border-stone-200 rounded-xl min-h-[60px] mt-1"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 sm:gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-11 sm:h-12 rounded-xl text-sm sm:text-base">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 h-11 sm:h-12 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm sm:text-base" data-testid="add-yard-submit">
              Add Apiary
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddYardDialog;
